import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Link, Navigate, useNavigate } from '@tanstack/react-router'
import {
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Download,
  Eye,
  EyeOff,
  FileCode,
  FileText,
  GripVertical,
  Plus,
  Receipt,
  Trash2,
  X,
} from 'lucide-react'
import { Fragment, useEffect, useState } from 'react'
import { InvoiceMailPanel } from '../components/invoices/invoice-mail-panel'
import { ProductAutocomplete } from '../components/product-autocomplete'
import { MobileBackTitle } from '../components/shell/mobile-back-title'
import { MobileBottomSlots, MobileTopCenter } from '../components/shell/mobile-shell-outlets'
import { Button } from '../components/ui/button'
import { confirm } from '../components/ui/confirm-dialog'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { BarAccentPlus, BarSlot } from '../components/ui/mobile-action-bar'
import { PageHeader } from '../components/ui/page-header'
import { Select } from '../components/ui/select'
import { SidePanel } from '../components/ui/side-panel'
import {
  api,
  type InvoiceDetail,
  type InvoiceLineItem,
  type InvoiceStatus,
  type UpdateInvoicePayload,
  type UpdateLineItemPayload,
} from '../lib/api'
import { formatCurrency } from '../lib/format'
import { notify } from '../lib/notify'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invoices/$id',
  component: InvoiceDetailPage,
})

const STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: 'Entwurf',
  sent: 'Versendet',
  paid: 'Bezahlt',
  cancelled: 'Storniert',
}

const STATUS_COLORS: Record<InvoiceStatus, string> = {
  draft: 'bg-bg-overlay text-text-secondary',
  sent: 'bg-accent/15 text-accent',
  paid: 'bg-emerald-500/15 text-emerald-500',
  cancelled: 'bg-red-500/15 text-red-500',
}

/** Group line items by their VAT rate and return per-rate net + tax cents.
 * Lines without an explicit rate fall back to the invoice default. */
function summarizeTaxByRate(
  items: InvoiceLineItem[],
  defaultRate: number,
): Array<{ rate: number; netCents: number; taxCents: number }> {
  const map = new Map<number, { netCents: number; taxCents: number }>()
  for (const it of items) {
    const rate = it.taxRatePercent != null ? Number(it.taxRatePercent) : defaultRate
    const bucket = map.get(rate) ?? { netCents: 0, taxCents: 0 }
    bucket.netCents += it.totalCents
    bucket.taxCents += Math.round((it.totalCents * rate) / 100)
    map.set(rate, bucket)
  }
  return Array.from(map.entries())
    .map(([rate, b]) => ({ rate, ...b }))
    .sort((a, b) => a.rate - b.rate)
}

function dateInputValue(s: string | null): string {
  if (!s) return ''
  return s.slice(0, 10)
}

const PREVIEW_PREF_KEY = 'embertime.invoice.previewOpen'

function InvoiceDetailPage() {
  const { id } = Route.useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const [previewOpen, setPreviewOpen] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true
    const stored = window.localStorage.getItem(PREVIEW_PREF_KEY)
    return stored === null ? true : stored === '1'
  })
  useEffect(() => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(PREVIEW_PREF_KEY, previewOpen ? '1' : '0')
  }, [previewOpen])

  const [downloadOpen, setDownloadOpen] = useState(false)
  // Mobile-Shell (<640): Export-Sheet hinterm Bottom-Slot (gleiche Ziele
  // wie das Desktop-Export-Dropdown), ＋ Position, ←+Rechnungsnr. oben.
  const [exportSheet, setExportSheet] = useState(false)
  const [previewToken, setPreviewToken] = useState(() => Date.now())
  const refreshPreview = () => {
    setPreviewToken(Date.now())
    qc.invalidateQueries({ queryKey: ['invoice', id, 'mail-state'] })
  }

  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })
  const { data, isLoading } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => api.getInvoice(id),
    enabled: !!meData?.user,
  })
  const { data: customersData } = useQuery({
    queryKey: ['customers', { archived: false }],
    queryFn: () => api.listCustomers(false),
    enabled: !!meData?.user,
  })

  const updateInvoiceMutation = useMutation({
    mutationFn: (patch: UpdateInvoicePayload) => api.updateInvoice(id, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoice', id] })
      qc.invalidateQueries({ queryKey: ['invoices'] })
      refreshPreview()
    },
    onError: (e: Error) => notify.error(e.message),
  })

  const addItemMutation = useMutation({
    mutationFn: () =>
      api.addInvoiceLineItem(id, {
        description: 'Neue Position',
        quantity: 1,
        unit: 'h',
        unitPriceCents: 0,
      }),
    onSuccess: (r) => {
      qc.setQueryData(['invoice', id], { invoice: r.invoice })
      qc.invalidateQueries({ queryKey: ['invoices'] })
      refreshPreview()
    },
    onError: (e: Error) => notify.error(e.message),
  })

  const updateItemMutation = useMutation({
    mutationFn: ({ itemId, patch }: { itemId: string; patch: UpdateLineItemPayload }) =>
      api.updateInvoiceLineItem(id, itemId, patch),
    onSuccess: (r) => {
      qc.setQueryData(['invoice', id], { invoice: r.invoice })
      qc.invalidateQueries({ queryKey: ['invoices'] })
      refreshPreview()
    },
    onError: (e: Error) => notify.error(e.message),
  })

  const deleteItemMutation = useMutation({
    mutationFn: (itemId: string) => api.deleteInvoiceLineItem(id, itemId),
    onSuccess: (r) => {
      qc.setQueryData(['invoice', id], { invoice: r.invoice })
      qc.invalidateQueries({ queryKey: ['invoices'] })
      refreshPreview()
    },
    onError: (e: Error) => notify.error(e.message),
  })

  const reorderItemsMutation = useMutation({
    mutationFn: (orderedIds: string[]) => api.reorderInvoiceLineItems(id, orderedIds),
    onSuccess: (r) => {
      qc.setQueryData(['invoice', id], { invoice: r.invoice })
      refreshPreview()
    },
    onError: (e: Error) => notify.error(e.message),
  })

  const deleteInvoiceMutation = useMutation({
    mutationFn: () => api.deleteInvoice(id),
    onSuccess: () => {
      notify.success('Rechnung gelöscht')
      qc.invalidateQueries({ queryKey: ['invoices'] })
      window.location.href = `${import.meta.env.BASE_URL}invoices`
    },
    onError: (e: Error) => notify.error(e.message),
  })

  // #1007: bei EMBERFIBU_RECHNUNGEN=exklusiv kein FastBill-Entwurf mehr.
  const { data: fibuStatus } = useQuery({
    queryKey: ['fibu-status'],
    queryFn: () => api.fibuStatus(),
    enabled: !!meData?.user,
  })
  const fastbillGesperrt = fibuStatus?.modus === 'exklusiv'

  const pushFastbillMutation = useMutation({
    mutationFn: () => api.pushInvoiceFastbill(id),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['invoice', id] })
      notify.success(
        r.alreadyPushed
          ? `Bereits in Fastbill (Entwurf #${r.fastbillInvoiceId})`
          : `Als Fastbill-Entwurf #${r.fastbillInvoiceId} angelegt`,
      )
    },
    onError: (e: Error) => notify.error(e.message),
  })

  if (meLoading || isLoading) return null
  if (!meData?.user) return <Navigate to="/login" />
  if (!data?.invoice) {
    return (
      <div className="space-y-4">
        <Link to="/invoices" className="text-sm text-text-tertiary hover:text-accent">
          ← zurück zur Liste
        </Link>
        <p className="text-text-secondary">Rechnung nicht gefunden.</p>
      </div>
    )
  }

  const inv = data.invoice
  const customers = customersData?.customers ?? []
  const isAdmin = meData.user.role === 'admin'
  const isLocked = inv.status === 'paid' || inv.status === 'cancelled'

  // Cache-buster as a query param (not a hash) — browsers don't reload an
  // iframe when only the fragment changes, so #v= used to keep showing the
  // stale PDF after edits.
  const previewBaseUrl = api.invoicePdfUrl(inv.id)
  const previewUrl = `${previewBaseUrl}${previewBaseUrl.includes('?') ? '&' : '?'}v=${previewToken}`

  const onMoveItem = (idx: number, dir: -1 | 1) => {
    const items = [...inv.lineItems]
    const target = idx + dir
    if (target < 0 || target >= items.length) return
    const tmp = items[idx]
    items[idx] = items[target]
    items[target] = tmp
    reorderItemsMutation.mutate(items.map((it) => it.id))
  }

  return (
    <div className="space-y-4 animate-fade-in pb-12">
      {/* ←+Rechnungsnr. oben: NUR Phone (Top-Leiste existiert im Tablet-Band
          nicht — dort trägt die Sticky-Bar den phone:hidden-Zurück-Link).
          Bottom-Slots (Phone + Tablet-Band): Export-Sheet + ＋ Position
          (bestehende Handler; Status/Fastbill/Vorschau bleiben in der
          Sticky-Bar im Content — im Band doppelt sich ＋ Position mit dem
          Content-Button, Daumen-Redundanz per Design). */}
      <MobileTopCenter>
        <MobileBackTitle
          title={inv.invoiceNumber}
          onBack={() => navigate({ to: '/invoices' })}
          backAriaLabel="Zurück zur Rechnungsliste"
        />
      </MobileTopCenter>

      <MobileBottomSlots>
        <BarSlot
          label="Export"
          icon={<Download className="h-5 w-5" />}
          ariaLabel="Rechnung exportieren"
          ariaHasPopup
          onClick={() => setExportSheet(true)}
        />
        <BarAccentPlus
          ariaLabel="Position hinzufügen"
          onClick={() => addItemMutation.mutate()}
          disabled={addItemMutation.isPending}
        />
      </MobileBottomSlots>

      {/* Export-Sheet (<640): identische Ziele wie das Desktop-Dropdown. */}
      <SidePanel open={exportSheet} onClose={() => setExportSheet(false)} title="Export">
        <div className="flex flex-col divide-y divide-border-subtle rounded-lg border border-border-subtle overflow-hidden">
          <DropdownItem
            icon={<FileText className="h-4 w-4" />}
            label="PDF herunterladen"
            sub="Klassische Rechnung"
            href={api.invoicePdfUrl(inv.id, { download: true })}
            onClick={() => setExportSheet(false)}
          />
          <DropdownItem
            icon={<FileText className="h-4 w-4 text-accent" />}
            label="ZUGFeRD-PDF"
            sub="PDF mit eingebetteter XML"
            href={api.invoicePdfUrl(inv.id, { format: 'zugferd', download: true })}
            onClick={() => setExportSheet(false)}
          />
          <DropdownItem
            icon={<FileCode className="h-4 w-4 text-accent" />}
            label="XRechnung (UBL 2.1)"
            sub="reine XML, B2G-tauglich"
            href={api.invoiceXRechnungUrl(inv.id)}
            onClick={() => setExportSheet(false)}
          />
          <DropdownItem
            icon={<FileCode className="h-4 w-4" />}
            label="ZUGFeRD-XML (Factur-X)"
            sub="reine CII-XML"
            href={api.invoiceZugferdXmlUrl(inv.id)}
            onClick={() => setExportSheet(false)}
          />
        </div>
      </SidePanel>

      {/* Sticky action bar — der Zurück-Link sitzt <640 in der Shell-Top-
          Leiste (phone:hidden); die übrigen Aktionen bleiben. */}
      <div className="sticky top-0 z-10 -mx-5 md:-mx-10 px-5 md:px-10 py-3 bg-bg-base/85 backdrop-blur-md border-b border-border-subtle">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <Link
            to="/invoices"
            className="inline-flex phone:hidden items-center gap-1.5 text-sm text-text-tertiary hover:text-accent transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> zurück zur Liste
          </Link>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="secondary"
              onClick={() => setPreviewOpen((v) => !v)}
              title={previewOpen ? 'Vorschau ausblenden' : 'Vorschau einblenden'}
            >
              {previewOpen ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              <span className="hidden sm:inline">
                {previewOpen ? 'Vorschau aus' : 'Vorschau ein'}
              </span>
            </Button>
            {!fastbillGesperrt && (
              <Button
                variant="secondary"
                onClick={() => pushFastbillMutation.mutate()}
                disabled={pushFastbillMutation.isPending || !!inv.fastbillId}
                title={
                  inv.fastbillId
                    ? `Bereits als Fastbill-Entwurf #${inv.fastbillId} angelegt`
                    : 'Als Entwurf an Fastbill senden'
                }
              >
                <Receipt className="h-4 w-4" />
                <span className="hidden sm:inline">
                  {inv.fastbillId ? 'In Fastbill' : 'An Fastbill'}
                </span>
              </Button>
            )}
            <Select
              value={inv.status}
              onChange={(e) =>
                updateInvoiceMutation.mutate({ status: e.target.value as InvoiceStatus })
              }
              className={`h-9 w-32 ${STATUS_COLORS[inv.status]}`}
              disabled={updateInvoiceMutation.isPending}
            >
              {(Object.keys(STATUS_LABELS) as InvoiceStatus[]).map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABELS[s]}
                </option>
              ))}
            </Select>
            <div className="relative">
              <Button onClick={() => setDownloadOpen((v) => !v)}>
                <Download className="h-4 w-4" /> Export
                <ChevronDown className="h-3.5 w-3.5" />
              </Button>
              {downloadOpen && (
                <>
                  <button
                    type="button"
                    aria-label="schließen"
                    className="fixed inset-0 z-30 cursor-default"
                    onClick={() => setDownloadOpen(false)}
                  />
                  <div className="absolute right-0 mt-1 z-40 w-64 rounded-lg border border-border-subtle bg-bg-elevated shadow-lg overflow-hidden">
                    <DropdownItem
                      icon={<FileText className="h-4 w-4" />}
                      label="PDF herunterladen"
                      sub="Klassische Rechnung"
                      href={api.invoicePdfUrl(inv.id, { download: true })}
                      onClick={() => setDownloadOpen(false)}
                    />
                    <DropdownItem
                      icon={<FileText className="h-4 w-4 text-accent" />}
                      label="ZUGFeRD-PDF"
                      sub="PDF mit eingebetteter XML"
                      href={api.invoicePdfUrl(inv.id, { format: 'zugferd', download: true })}
                      onClick={() => setDownloadOpen(false)}
                    />
                    <DropdownItem
                      icon={<FileCode className="h-4 w-4 text-accent" />}
                      label="XRechnung (UBL 2.1)"
                      sub="reine XML, B2G-tauglich"
                      href={api.invoiceXRechnungUrl(inv.id)}
                      onClick={() => setDownloadOpen(false)}
                    />
                    <DropdownItem
                      icon={<FileCode className="h-4 w-4" />}
                      label="ZUGFeRD-XML (Factur-X)"
                      sub="reine CII-XML"
                      href={api.invoiceZugferdXmlUrl(inv.id)}
                      onClick={() => setDownloadOpen(false)}
                    />
                  </div>
                </>
              )}
            </div>
            {isAdmin && (
              <Button
                variant="danger"
                onClick={async () => {
                  const ok = await confirm({
                    title: 'Rechnung löschen?',
                    description: `${inv.invoiceNumber} wird unwiderruflich gelöscht.`,
                    confirmLabel: 'Löschen',
                    variant: 'danger',
                  })
                  if (ok) deleteInvoiceMutation.mutate()
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Einreihiger Standard-Kopf statt Serif-Display (Ticket „obere Leiste
          einheitlich"): Nummer als Titel, Kunde · Betrag als Inline-Info —
          bleibt auch mobil sichtbar (keep-row; die Nummer sitzt <640 in der
          Shell-Top-Leiste). */}
      <PageHeader
        title={inv.invoiceNumber}
        eyebrow="Rechnung"
        phoneMode="keep-row"
        className="pt-2 border-b-0 pb-0"
        inline={
          // Name truncated, Betrag shrink-0 — sonst verschluckt ein langer
          // Kundenname am Phone zuerst den Betrag (Review-Fund).
          <p className="flex min-w-0 items-baseline gap-1 text-text-secondary">
            <span className="min-w-0 truncate">{inv.customerName}</span>
            <span className="shrink-0">· {formatCurrency(inv.totalCents, inv.currency)}</span>
          </p>
        }
      />

      {isLocked && (
        <div className="rounded-lg border border-border-subtle bg-bg-overlay/30 px-4 py-2.5 text-sm text-text-secondary">
          Diese Rechnung hat den Status <strong>{STATUS_LABELS[inv.status]}</strong>. Änderungen
          sind weiterhin möglich, aber prüfe Compliance vor dem Speichern.
        </div>
      )}

      <div className="max-w-4xl">
        <InvoiceMailPanel invoice={inv} />
      </div>

      {/* Vertical stack: Stammdaten → PDF-Vorschau → Positionen → Notizen */}
      <div className="space-y-5 max-w-4xl">
        <InvoiceHeaderEditor
          invoice={inv}
          customers={customers}
          onUpdate={(patch) => updateInvoiceMutation.mutate(patch)}
          isPending={updateInvoiceMutation.isPending}
        />

        {previewOpen && (
          <section className="rounded-2xl border border-border-subtle bg-bg-elevated/40 overflow-hidden">
            <header className="px-4 py-2 border-b border-border-subtle bg-bg-overlay/30 flex items-center justify-between">
              <span className="text-xs uppercase tracking-wider text-text-tertiary">
                PDF-Vorschau
              </span>
              <div className="flex items-center gap-3 text-xs">
                <button
                  type="button"
                  onClick={refreshPreview}
                  className="text-text-tertiary hover:text-accent"
                >
                  Neu laden
                </button>
                <a
                  href={api.invoicePdfUrl(inv.id)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-text-tertiary hover:text-accent inline-flex items-center gap-1"
                >
                  <ArrowRight className="h-3 w-3" /> Tab
                </a>
              </div>
            </header>
            <iframe
              key={previewToken}
              src={previewUrl}
              title={`Rechnung ${inv.invoiceNumber}`}
              className="w-full h-[700px] bg-white"
            />
          </section>
        )}

        <LineItemsEditor
          invoice={inv}
          onAddItem={() => addItemMutation.mutate()}
          onUpdateItem={(itemId, patch) => updateItemMutation.mutate({ itemId, patch })}
          onDeleteItem={async (itemId, label) => {
            const ok = await confirm({
              title: 'Position löschen?',
              description: label ? `Position „${label}“ wird entfernt.` : 'Position wird entfernt.',
              confirmLabel: 'Löschen',
              variant: 'danger',
            })
            if (ok) deleteItemMutation.mutate(itemId)
          }}
          onMoveItem={onMoveItem}
          onReorder={(orderedIds) => reorderItemsMutation.mutate(orderedIds)}
          mutating={
            addItemMutation.isPending ||
            updateItemMutation.isPending ||
            deleteItemMutation.isPending ||
            reorderItemsMutation.isPending
          }
        />

        <NotesEditor invoice={inv} onSave={(notes) => updateInvoiceMutation.mutate({ notes })} />
      </div>
    </div>
  )
}

// === Sub-components ===

function InvoiceHeaderEditor({
  invoice,
  customers,
  onUpdate,
  isPending,
}: {
  invoice: InvoiceDetail
  customers: { id: string; name: string }[]
  onUpdate: (patch: UpdateInvoicePayload) => void
  isPending: boolean
}) {
  const [form, setForm] = useState({
    invoiceNumber: invoice.invoiceNumber,
    customerId: invoice.customerId,
    issueDate: dateInputValue(invoice.issueDate),
    dueDate: dateInputValue(invoice.dueDate),
    periodFrom: dateInputValue(invoice.periodFrom),
    periodTo: dateInputValue(invoice.periodTo),
    taxRatePercent: Number(invoice.taxRatePercent),
    currency: invoice.currency,
  })

  // Re-sync local state when server-side invoice changes (after save / status switch)
  useEffect(() => {
    setForm({
      invoiceNumber: invoice.invoiceNumber,
      customerId: invoice.customerId,
      issueDate: dateInputValue(invoice.issueDate),
      dueDate: dateInputValue(invoice.dueDate),
      periodFrom: dateInputValue(invoice.periodFrom),
      periodTo: dateInputValue(invoice.periodTo),
      taxRatePercent: Number(invoice.taxRatePercent),
      currency: invoice.currency,
    })
  }, [invoice])

  function commitField<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((f) => ({ ...f, [key]: value }))
  }

  function saveOnBlur(patch: UpdateInvoicePayload) {
    // Only fire if a field actually changed compared to invoice
    const changed: UpdateInvoicePayload = {}
    for (const [k, v] of Object.entries(patch) as [keyof UpdateInvoicePayload, unknown][]) {
      if (v === undefined) continue
      const current = (invoice as unknown as Record<string, unknown>)[k]
      // Normalize date comparison
      if (
        (k === 'issueDate' || k === 'dueDate' || k === 'periodFrom' || k === 'periodTo') &&
        typeof v === 'string'
      ) {
        const cur = dateInputValue((current as string | null) ?? null)
        if (cur === v) continue
        ;(changed as Record<string, unknown>)[k] = v || null
        continue
      }
      if (k === 'taxRatePercent') {
        if (Number(current) === Number(v)) continue
      } else if (current === v) {
        continue
      }
      ;(changed as Record<string, unknown>)[k] = v
    }
    if (Object.keys(changed).length === 0) return
    onUpdate(changed)
  }

  return (
    <section className="rounded-2xl border border-border-subtle bg-bg-elevated/40 p-4 space-y-4">
      <header className="flex items-center justify-between">
        <h2 className="text-sm font-medium uppercase tracking-wider text-text-tertiary">
          Stammdaten
        </h2>
        {isPending && <span className="text-xs text-text-tertiary">Speichere …</span>}
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label>Rechnungs-Nr.</Label>
          <Input
            value={form.invoiceNumber}
            onChange={(e) => commitField('invoiceNumber', e.target.value)}
            onBlur={() => saveOnBlur({ invoiceNumber: form.invoiceNumber })}
          />
        </div>
        <div className="space-y-1">
          <Label>Kunde</Label>
          <Select
            value={form.customerId}
            onChange={(e) => {
              const v = e.target.value
              commitField('customerId', v)
              saveOnBlur({ customerId: v })
            }}
          >
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
            {/* Fallback: archived/deleted current customer */}
            {!customers.some((c) => c.id === form.customerId) && (
              <option value={form.customerId}>{invoice.customerName ?? '—'}</option>
            )}
          </Select>
        </div>
        <div className="space-y-1">
          <Label>Datum</Label>
          <Input
            type="date"
            value={form.issueDate}
            onChange={(e) => commitField('issueDate', e.target.value)}
            onBlur={() => saveOnBlur({ issueDate: form.issueDate })}
          />
        </div>
        <div className="space-y-1">
          <Label>Fällig</Label>
          <Input
            type="date"
            value={form.dueDate}
            onChange={(e) => commitField('dueDate', e.target.value)}
            onBlur={() => saveOnBlur({ dueDate: form.dueDate || null })}
          />
        </div>
        <div className="space-y-1">
          <Label>Leistung von</Label>
          <Input
            type="date"
            value={form.periodFrom}
            onChange={(e) => commitField('periodFrom', e.target.value)}
            onBlur={() => saveOnBlur({ periodFrom: form.periodFrom || null })}
          />
        </div>
        <div className="space-y-1">
          <Label>Leistung bis</Label>
          <Input
            type="date"
            value={form.periodTo}
            onChange={(e) => commitField('periodTo', e.target.value)}
            onBlur={() => saveOnBlur({ periodTo: form.periodTo || null })}
          />
        </div>
        <div className="space-y-1">
          <Label>MwSt %</Label>
          <Input
            type="number"
            step="0.5"
            min="0"
            value={form.taxRatePercent}
            onChange={(e) => commitField('taxRatePercent', Number(e.target.value))}
            onBlur={() => saveOnBlur({ taxRatePercent: form.taxRatePercent })}
          />
        </div>
        <div className="space-y-1">
          <Label>Währung</Label>
          <Input
            value={form.currency}
            onChange={(e) => commitField('currency', e.target.value)}
            onBlur={() => saveOnBlur({ currency: form.currency })}
            maxLength={3}
          />
        </div>
      </div>
    </section>
  )
}

function LineItemsEditor({
  invoice,
  onAddItem,
  onUpdateItem,
  onDeleteItem,
  onMoveItem,
  onReorder,
  mutating,
}: {
  invoice: InvoiceDetail
  onAddItem: () => void
  onUpdateItem: (itemId: string, patch: UpdateLineItemPayload) => void
  onDeleteItem: (itemId: string, label: string) => void
  onMoveItem: (idx: number, dir: -1 | 1) => void
  onReorder: (orderedIds: string[]) => void
  mutating: boolean
}) {
  // Optimistic local copy: dnd-kit moves rows visually during drag, but the
  // final state needs a frame after drop before the server round-trips.
  const [items, setItems] = useState(invoice.lineItems)
  useEffect(() => {
    setItems(invoice.lineItems)
  }, [invoice.lineItems])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIdx = items.findIndex((i) => i.id === active.id)
    const newIdx = items.findIndex((i) => i.id === over.id)
    if (oldIdx === -1 || newIdx === -1) return
    const next = arrayMove(items, oldIdx, newIdx)
    setItems(next)
    onReorder(next.map((i) => i.id))
  }

  return (
    <section className="rounded-2xl border border-border-subtle bg-bg-elevated/40 overflow-hidden">
      <header className="flex items-center justify-between px-4 py-3 border-b border-border-subtle bg-bg-overlay/30">
        <h2 className="text-sm font-medium uppercase tracking-wider text-text-tertiary">
          Positionen
        </h2>
        <div className="flex items-center gap-3">
          {mutating && <span className="text-xs text-text-tertiary">Speichere …</span>}
          <Button onClick={onAddItem} size="sm" variant="secondary">
            <Plus className="h-3.5 w-3.5" /> Position
          </Button>
        </div>
      </header>

      {items.length === 0 ? (
        <div className="px-4 py-12 text-center">
          <p className="text-sm text-text-tertiary">Noch keine Positionen.</p>
          <Button onClick={onAddItem} className="mt-3" size="sm">
            <Plus className="h-3.5 w-3.5" /> Erste Position hinzufügen
          </Button>
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
            <ul className="divide-y divide-border-subtle">
              {items.map((item, idx) => (
                <SortableLineItemRow
                  key={item.id}
                  item={item}
                  currency={invoice.currency}
                  defaultTaxRate={Number(invoice.taxRatePercent)}
                  isFirst={idx === 0}
                  isLast={idx === items.length - 1}
                  onUpdate={(patch) => onUpdateItem(item.id, patch)}
                  onDelete={() => onDeleteItem(item.id, item.description)}
                  onMoveUp={() => onMoveItem(idx, -1)}
                  onMoveDown={() => onMoveItem(idx, 1)}
                  position={idx + 1}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}

      {invoice.lineItems.length > 0 && (
        <footer className="border-t border-border-subtle bg-bg-overlay/20 px-4 py-3">
          <dl className="grid grid-cols-[1fr_auto] gap-x-6 gap-y-1.5 text-sm max-w-sm ml-auto tabular-nums">
            <dt className="text-text-secondary">Zwischensumme</dt>
            <dd className="text-right">
              {formatCurrency(invoice.subtotalCents, invoice.currency)}
            </dd>
            {/* MwSt-Aufschlüsselung pro Satz: rechnet aus den Line-Items neu,
                damit die einzelnen Sätze (z.B. 19%/7%/0%) sichtbar sind. */}
            {summarizeTaxByRate(invoice.lineItems, Number(invoice.taxRatePercent)).map((b) => (
              <Fragment key={b.rate}>
                <dt className="text-text-secondary">
                  MwSt {b.rate.toFixed(b.rate % 1 === 0 ? 0 : 2)} %{' '}
                  <span className="text-text-tertiary text-xs">
                    auf {formatCurrency(b.netCents, invoice.currency)}
                  </span>
                </dt>
                <dd className="text-right">{formatCurrency(b.taxCents, invoice.currency)}</dd>
              </Fragment>
            ))}
            <dt className="font-medium text-text-primary">Gesamt</dt>
            <dd className="text-right font-bold text-accent text-base">
              {formatCurrency(invoice.totalCents, invoice.currency)}
            </dd>
          </dl>
        </footer>
      )}
    </section>
  )
}

function SortableLineItemRow(props: {
  item: InvoiceLineItem
  currency: string
  defaultTaxRate: number
  isFirst: boolean
  isLast: boolean
  onUpdate: (patch: UpdateLineItemPayload) => void
  onDelete: () => void
  onMoveUp: () => void
  onMoveDown: () => void
  position: number
}) {
  const {
    item,
    currency,
    defaultTaxRate,
    isFirst,
    isLast,
    onUpdate,
    onDelete,
    onMoveUp,
    onMoveDown,
    position,
  } = props
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 20 : undefined,
    opacity: isDragging ? 0.85 : 1,
    boxShadow: isDragging ? '0 12px 32px -8px rgb(0 0 0 / 0.45)' : undefined,
    background: isDragging ? 'rgb(var(--c-bg-elevated))' : undefined,
  }

  const initialTaxRate = item.taxRatePercent != null ? Number(item.taxRatePercent) : defaultTaxRate
  const [draft, setDraft] = useState({
    description: item.description,
    quantity: Number(item.quantity),
    unit: item.unit,
    unitPriceCents: item.unitPriceCents,
    taxRatePercent: initialTaxRate,
  })

  // Sync if server-side item changes (e.g. after reorder)
  useEffect(() => {
    setDraft({
      description: item.description,
      quantity: Number(item.quantity),
      unit: item.unit,
      unitPriceCents: item.unitPriceCents,
      taxRatePercent: item.taxRatePercent != null ? Number(item.taxRatePercent) : defaultTaxRate,
    })
  }, [item, defaultTaxRate])

  function commit<K extends keyof typeof draft>(key: K, value: (typeof draft)[K]) {
    setDraft((d) => ({ ...d, [key]: value }))
  }

  function saveDescription() {
    if (draft.description.trim() && draft.description !== item.description) {
      onUpdate({ description: draft.description.trim() })
    }
  }
  function saveQuantity() {
    if (draft.quantity > 0 && draft.quantity !== Number(item.quantity)) {
      onUpdate({ quantity: draft.quantity })
    }
  }
  function saveUnit() {
    if (draft.unit && draft.unit !== item.unit) {
      onUpdate({ unit: draft.unit })
    }
  }
  function saveUnitPrice() {
    if (draft.unitPriceCents >= 0 && draft.unitPriceCents !== item.unitPriceCents) {
      onUpdate({ unitPriceCents: draft.unitPriceCents })
    }
  }
  function saveTaxRate() {
    if (
      draft.taxRatePercent >= 0 &&
      draft.taxRatePercent <= 100 &&
      draft.taxRatePercent !== initialTaxRate
    ) {
      onUpdate({ taxRatePercent: draft.taxRatePercent })
    }
  }

  const lineTotalCents = Math.round(draft.quantity * draft.unitPriceCents)

  return (
    <li ref={setNodeRef} style={style} className="px-4 py-3 group bg-bg-elevated/0">
      <div className="grid grid-cols-[auto_auto_1fr_auto] gap-3 items-start">
        {/* Drag handle */}
        <button
          type="button"
          {...attributes}
          {...listeners}
          aria-label="Position per Drag verschieben"
          className="mt-1 -mx-1 p-1 text-text-tertiary hover:text-accent hover:bg-bg-overlay rounded cursor-grab active:cursor-grabbing touch-none"
          style={{ touchAction: 'none' }}
        >
          <GripVertical className="h-4 w-4" />
        </button>

        {/* Reorder buttons (kept for keyboard / a11y) */}
        <div className="flex flex-col gap-0.5 mt-1">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={isFirst}
            aria-label="Nach oben"
            className="p-0.5 text-text-tertiary hover:text-accent disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronUp className="h-3.5 w-3.5" />
          </button>
          <span className="text-2xs text-text-tertiary tabular-nums text-center">{position}</span>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={isLast}
            aria-label="Nach unten"
            className="p-0.5 text-text-tertiary hover:text-accent disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* Editable fields */}
        <div className="space-y-1.5 min-w-0">
          <ProductAutocomplete
            value={draft.description}
            onChange={(v) => commit('description', v)}
            onPick={(p) => {
              const desc = p.description?.trim() ? `${p.name} — ${p.description}` : p.name
              setDraft((d) => ({
                ...d,
                description: desc,
                unitPriceCents: p.defaultPriceCents,
                unit: p.unit,
              }))
              // Persist all fields + the productId origin immediately; the
              // onBlur path can't batch them because the input is already
              // losing focus.
              onUpdate({
                description: desc,
                unitPriceCents: p.defaultPriceCents,
                unit: p.unit,
                productId: p.id,
              })
            }}
            onBlur={saveDescription}
            placeholder="Beschreibung"
            className="bg-transparent border-b border-transparent hover:border-border-subtle focus:border-accent/60 focus:outline-none text-text-primary py-0.5 transition-colors"
          />
          <div className="flex items-center gap-3 text-sm flex-wrap">
            <span className="inline-flex items-center gap-1.5">
              <input
                type="number"
                step="0.25"
                min="0"
                value={draft.quantity}
                onChange={(e) => commit('quantity', Number(e.target.value))}
                onBlur={saveQuantity}
                className="w-20 bg-bg-overlay/40 rounded px-2 py-0.5 text-right tabular-nums focus:outline-none focus:ring-1 focus:ring-accent/40"
              />
              <input
                value={draft.unit}
                onChange={(e) => commit('unit', e.target.value)}
                onBlur={saveUnit}
                className="w-12 bg-transparent text-text-tertiary text-xs focus:outline-none"
                maxLength={6}
              />
            </span>
            <span className="text-text-tertiary">×</span>
            <span className="inline-flex items-center gap-1.5">
              <input
                type="number"
                step="0.01"
                min="0"
                value={(draft.unitPriceCents / 100).toFixed(2)}
                onChange={(e) => commit('unitPriceCents', Math.round(Number(e.target.value) * 100))}
                onBlur={saveUnitPrice}
                className="w-24 bg-bg-overlay/40 rounded px-2 py-0.5 text-right tabular-nums focus:outline-none focus:ring-1 focus:ring-accent/40"
              />
              <span className="text-text-tertiary text-xs">{currency}</span>
            </span>
            <span className="text-text-tertiary">·</span>
            <span className="inline-flex items-center gap-1.5">
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={draft.taxRatePercent}
                onChange={(e) => commit('taxRatePercent', Number(e.target.value))}
                onBlur={saveTaxRate}
                className="w-16 bg-bg-overlay/40 rounded px-2 py-0.5 text-right tabular-nums focus:outline-none focus:ring-1 focus:ring-accent/40"
                title="MwSt-Satz für diese Position"
              />
              <span className="text-text-tertiary text-xs">% MwSt</span>
            </span>
          </div>
        </div>

        {/* Total + delete */}
        <div className="flex items-start gap-2">
          <div className="text-right">
            <p className="font-mono font-medium text-text-primary tabular-nums">
              {formatCurrency(lineTotalCents, currency)}
            </p>
            {item.timeEntryId ? (
              <p className="mt-0.5 text-2xs text-text-tertiary">aus Zeiteintrag</p>
            ) : item.productId ? (
              <p className="mt-0.5 text-2xs text-accent/70">aus Produkte</p>
            ) : null}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onDelete}
            aria-label="Position löschen"
            className="h-7 w-7 text-text-tertiary hover:text-danger opacity-40 group-hover:opacity-100 touch:opacity-100"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </li>
  )
}

function NotesEditor({
  invoice,
  onSave,
}: {
  invoice: InvoiceDetail
  onSave: (notes: string | null) => void
}) {
  const [notes, setNotes] = useState(invoice.notes ?? '')
  useEffect(() => {
    setNotes(invoice.notes ?? '')
  }, [invoice])

  return (
    <section className="rounded-2xl border border-border-subtle bg-bg-elevated/40 p-4 space-y-2">
      <h2 className="text-sm font-medium uppercase tracking-wider text-text-tertiary">Hinweise</h2>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        onBlur={() => {
          const next = notes.trim() === '' ? null : notes
          if ((invoice.notes ?? '') === (next ?? '')) return
          onSave(next)
        }}
        rows={3}
        placeholder="Zahlungsziel 14 Tage netto, Rechnungsbetrag bitte unter Angabe der Rechnungsnummer überweisen."
        className="w-full rounded-lg border border-border-default bg-bg-overlay/50 px-3 py-2 text-base lg:text-sm text-text-primary focus:border-accent/60 focus:outline-none focus:ring-2 focus:ring-accent/20 resize-y"
      />
    </section>
  )
}

function DropdownItem({
  icon,
  label,
  sub,
  href,
  onClick,
}: {
  icon: React.ReactNode
  label: string
  sub: string
  href: string
  onClick?: () => void
}) {
  return (
    <a
      href={href}
      onClick={onClick}
      target="_blank"
      rel="noreferrer"
      className="flex items-start gap-3 px-3 py-2.5 hover:bg-bg-overlay/40 transition-colors"
    >
      <div className="mt-0.5">{icon}</div>
      <div className="min-w-0">
        <div className="text-sm font-medium text-text-primary">{label}</div>
        <div className="text-xs text-text-tertiary">{sub}</div>
      </div>
    </a>
  )
}
